/**
 * The payment note on a driver's session (2026-10-22).
 *
 * The detail page said "Paid with your card on file" whenever
 * stripe_payment_intent_id was set. The payment_intent.canceled webhook
 * stamps that id too, so a start the charger refused, whose hold was
 * cancelled, told the driver they had paid. Warren's ruling: a session that
 * never started carries no charge and says so, "No charge", or "Released"
 * when a pre-auth hold was placed and let go.
 *
 * "Never started" is the transaction id, not the status: a real session
 * that lost power also ends 'failed', and it bills. A shell the charger
 * never promoted keeps its 'pending-' placeholder (or the legacy ''). A
 * completed session that cost nothing but had a hold reads "Released" too,
 * since its hold was cancelled at stop. Anything else, and anything with
 * collected money behind it, keeps the old note.
 */

export type SessionPaymentNote = 'paid' | 'wallet' | 'pending' | 'no_charge' | 'released' | 'v1_paid' | 'v1_billed' | 'v1_collected' | null;

export interface SessionPaymentFacts {
  status: string;
  transaction_id?: string | null;
  cost?: { total?: number | null } | null;
  stripe_payment_intent_id?: string | null;
  pre_auth_payment_intent_id?: string | null;
  payment_status?: string | null;
  stripe_receipt_url?: string | null;
  wallet_debited?: boolean | null;
  wallet_debit_cents?: number | null;
  manual_review_required?: boolean | null;
  mirrored_from_v1?: boolean | null;
  v1_payment_status?: string | null;
  /** Set when the 2026-10-23 flip clean-up collected the v1 session on v3's Stripe. */
  v1_cleanup?: { payment_intent?: string | null } | null;
}

const IN_FLIGHT = new Set(['active', 'pending', 'sweeping']);

/** No real OCPP transaction: the shell placeholder or the legacy ''. A
 *  session still in flight is not "never started" yet. */
export function sessionNeverStarted(s: Pick<SessionPaymentFacts, 'status' | 'transaction_id'>): boolean {
  if (IN_FLIGHT.has(s.status)) return false;
  return !s.transaction_id || s.transaction_id.startsWith('pending-');
}

function moneyCollected(s: SessionPaymentFacts): boolean {
  return s.payment_status === 'captured'
    || Boolean(s.stripe_receipt_url)
    || s.wallet_debited === true
    || (s.wallet_debit_cents ?? 0) > 0;
}

/** A start that never happened and took no money: history shows
 *  "No charge" where the amount would be. */
export function sessionNeverStartedNoCharge(s: SessionPaymentFacts): boolean {
  return sessionNeverStarted(s) && !moneyCollected(s);
}

function holdReleased(s: SessionPaymentFacts): boolean {
  // Only on proof the hold was let go: StopTransaction's 'cancelled_zero_cost'.
  // A cancel that failed at Stripe leaves the same fields as one that worked,
  // so a refused start says "No charge", true either way.
  return s.payment_status === 'cancelled_zero_cost';
}

export function sessionPaymentNote(s: SessionPaymentFacts): SessionPaymentNote {
  // v1 history (2026-10-23): billed by v1, no v3 payment fields, so the rule
  // below read every one "Payment pending" and asked for a card. It is never
  // collected on v3; say where it was billed instead.
  if (s.mirrored_from_v1 === true) {
    // Collected by the flip clean-up on v3 after the move, whatever v1 still says.
    if (s.v1_cleanup?.payment_intent) return 'v1_collected';
    return /^(success|succeeded|paid)$/i.test(s.v1_payment_status ?? '') ? 'v1_paid' : 'v1_billed';
  }
  if (IN_FLIGHT.has(s.status)) return null;
  const total = s.cost?.total ?? 0;
  if (!moneyCollected(s)) {
    if (sessionNeverStarted(s)) return holdReleased(s) ? 'released' : 'no_charge';
    if (!(total > 0) && holdReleased(s)) return 'released';
  }
  if (s.stripe_payment_intent_id && (total > 0 || moneyCollected(s))) return 'paid';
  // The wallet paid the whole price (no card leg): "Paid from your wallet",
  // never "Payment pending" (2026-10-25, beside the converted wallet line).
  // wallet_debit_cents is in the session's currency, as cost.total is.
  if (total > 0 && s.wallet_debited === true && (s.wallet_debit_cents ?? 0) >= total) return 'wallet';
  return total > 0 ? 'pending' : null;
}
